import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  DeleteDocumentRequest,
  DOCUMENT_TYPE,
  Documents,
  GetPolicyAcknowledgementFormRequest,
  GetPolicyAcknowledgementFormResponse,
  GetPolicyDocumentsByPolicyIDRequest,
  PolicyAcknowledgementFormValues,
} from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { PolicyPromiseClient } from '@trustero/trustero-api-web/lib/model/policy_grpc_web_pb'
import { AttachmentPromiseClient } from '@trustero/trustero-api-web/lib/attachment/attachment_grpc_web_pb'
import {
  CreateOrUpdatePolicyRequest,
  GetPolicyRequest,
  ListPoliciesRequest,
  POLICY_READ_ONLY_FIELDS,
  PolicyReadOnlyField,
  PolicyReadOnlyFields,
  PolicyRecord,
  UpdatePolicyLabelsRequest,
  UpdatePolicyReadOnlyFieldsRequest,
} from '@trustero/trustero-api-web/lib/model/policy_pb'
import {
  BoolValue,
  StringValue,
} from 'google-protobuf/google/protobuf/wrappers_pb'
import log from 'loglevel'
import { useLocation, useNavigate } from 'react-router-dom'
import { NewGrpcResponse, GrpcResponse } from 'src/components/async/hooks/types'
import { GetPolicyFrameworksRequest } from '@trustero/trustero-api-web/lib/model/policy_pb'
import { Frameworks } from '@trustero/trustero-api-web/lib/model/framework_pb'
import {
  useFetchDocumentBody,
  useGetLinkedDocumentUrl,
} from 'src/components/async/document/useDocument'
import { openInNewTab } from 'src/Utils/globalHelpers'
import { ConfirmationContext } from 'src/Confirmation/ConfirmationContext'
import { useConfirmationModal } from 'src/components/ModalForms'
import { isMarkdown } from 'src/components/ModalForms/FileType/useFileTypeIcon'
import { PolicyAcknowledgementFormData } from 'src/context/FormContext/PolicyAcknowledgementContext'
import { LINKER_MODEL_IDS } from 'src/lib/Linkers/Linkers.constants'
import {
  PickerConfiguration,
  GOOGLE_PICKER_SUPPORTED_FILE_TYPES,
} from 'src/lib/Linkers/modals/Google Drive Wizard/GoogleDriveWizard.constants'
import {
  GRC_PLATFORM,
  ReadOnlyFieldDetail,
} from '@trustero/trustero-api-web/lib/common/grc_platform_pb'
import { useImportedRecordModal } from 'src/components/ModalForms/useImportedRecordModal'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { useLoadGoogleApi } from 'src/lib/Linkers/modals/Google Drive Wizard/GoogleDriveWizard.hooks'
import { useGrpcRevalidateByMethod } from 'src/components/async/useGrpcMutate'
import { useNewModel } from 'src/components/async/hooks/useNewModel'
import { ModalFormId } from 'src/components/ModalForms'
import { SearchableData } from 'src/Utils/search/search.types'
import { prepareSearchableData } from 'src/Utils/search/search.helpers'
import { useComplianceFrameworks } from 'src/components/async/complianceframework'
import { useSwrImmutableGrpc } from '../../components/async/useSwrImmutableGrpc'
import { ToastPrompts, showInfoToast } from '../../Utils/helpers/toast'
import { useAuthorizedGrpcClient } from '../../adapter'
import {
  useInvalidatePoliciesCache,
  usePolicies,
} from '../../components/async/policy/usePolicies'
import { useInAudit } from '../../context/AuditContext'
import {
  useInvalidateDirectoryScanSettings,
  useListDirectoryScanSettings,
} from '../Evidence/evidence.hooks'
import { useInProgressReceptorScans } from '../Receptors/receptors.hooks'
import { useDocumentOnClick } from './PoliciesShowPage/DocumentGrid/DocumentItem/useDocumentOnClick'
import {
  editDocumentMarkdown,
  getArePolicySnapshotsEqual,
  getPolicyFieldSnapshot,
  isAnyPolicyIndexing,
  policyFieldsChanged,
  PolicyFieldSnapshot,
  snapshotPolicies,
} from './Policies.helpers'
import { MAX_STABLE_POLLS, POLL_INTERVAL_MS } from './Policies.constants'
import { PolicyListSnapshot } from './Policies.types'

const policyPollRestartCallbacks = new Set<() => void>()

export const restartPolicyPolling = (): void => {
  policyPollRestartCallbacks.forEach((cb) => cb())
}

// [AP-4811] TODO: Move all policy hooks into this file

/**
 * This hook returns a function that can be used to add or update a policy.
 * We can add more parameters as needed as we continue to use it.
 */

export const useAddOrUpdatePolicy = (): (({
  modelId,
  description,
}: {
  modelId: string
  description: string
}) => Promise<PolicyRecord>) => {
  const mutator = useInvalidatePoliciesCache()
  const policyClient = useAuthorizedGrpcClient(PolicyPromiseClient)

  return async ({
    modelId,
    description,
  }: {
    modelId: string
    description: string
  }): Promise<PolicyRecord> => {
    const request = new CreateOrUpdatePolicyRequest()

    if (modelId) {
      request.setModelId(modelId)
    }
    if (description) {
      request.setDescription(new StringValue().setValue(description))
    }

    const response = await policyClient.createOrUpdate(request)
    await mutator()
    return response
  }
}

export const useUpdatePolicyLabels = (): (({
  policyId,
  labelIds,
}: {
  policyId: string
  labelIds: string[]
}) => Promise<PolicyRecord>) => {
  const mutator = useInvalidatePoliciesCache()
  const policyClient = useAuthorizedGrpcClient(PolicyPromiseClient)

  return async ({
    policyId,
    labelIds,
  }: {
    policyId: string
    labelIds: string[]
  }): Promise<PolicyRecord> => {
    const request = new UpdatePolicyLabelsRequest()
      .setPolicyId(policyId)
      .setLabelIdsList(labelIds)

    const response = await policyClient.updatePolicyLabels(request)
    await mutator()
    return response
  }
}

/**
 * This hook returns a function that can be used to generate a policy acknowledgement
 * form. If the return function's override parameter is not set to true, the function
 * will return a response that indicates whether any in-scope policies are missing
 * documentation in a supported format (link, markdown, pdf or docx). If no policies
 * are missing documents, or if override is set to true, the function will return a
 * policy acknowledgement form.
 * @returns {Function} A function that accepts an override parameter and returns a policy
 *                      acknowledgement form or a response indicating missing documentation.
 */

export const useGetPolicyAcknowledgementForm = (): ((
  policyIds: string[],
  override: boolean,
  includeText: boolean,
  formData: PolicyAcknowledgementFormData,
) => Promise<GetPolicyAcknowledgementFormResponse>) => {
  const { auditId } = useInAudit()
  const attachmentClient = useAuthorizedGrpcClient(AttachmentPromiseClient)
  return async (
    policyIds: string[],
    override: boolean,
    includeText: boolean,
    formData: PolicyAcknowledgementFormData,
  ) => {
    const request = new GetPolicyAcknowledgementFormRequest()
    auditId && request.setAuditId(new StringValue().setValue(auditId))
    override && request.setOverride(new BoolValue().setValue(override))
    includeText && request.setIncludeText(new BoolValue().setValue(includeText))
    request.setPolicyIdsList(policyIds)
    request.setFormData(
      new PolicyAcknowledgementFormValues()
        .setCompanyName(formData.companyName)
        .setTitle(formData.title)
        .setText(formData.text),
    )
    let response
    try {
      response = await attachmentClient.getPolicyAcknowledgementForm(request)
    } catch (err) {
      const errString = auditId
        ? `Error generating policy acknowledgement form for audit ${auditId}: `
        : 'Error generating policy acknowledgement form: '
      log.error(errString, err)
      showInfoToast(ToastPrompts.POLICY_ACKNOWLEDGEMENT_FORM_ERROR)
    }

    return response || new GetPolicyAcknowledgementFormResponse()
  }
}

export const useGetPolicyFrameworks = (
  policyId: string,
): GrpcResponse<Frameworks> => {
  const newModel = useNewModel()
  const request = new GetPolicyFrameworksRequest().setId(policyId)
  if (newModel) {
    request.setUseNewModel(newModel)
  }
  const { response } = useSwrImmutableGrpc(
    PolicyPromiseClient.prototype.getFrameworks,
    request,
  )
  return NewGrpcResponse(response)
}

export const useGetPolicyDocumentsByModelId = (
  policyModelId = '',
  policyExists = true,
): GrpcResponse<Documents> => {
  const { auditId } = useInAudit()

  const request = new GetPolicyDocumentsByPolicyIDRequest().setPolicyModelId(
    policyModelId,
  )

  if (auditId) {
    request.setAuditId(new StringValue().setValue(auditId))
  }

  request.setUseNewModel(true)

  const { response } = useSwrImmutableGrpc(
    AttachmentPromiseClient.prototype.getPolicyDocumentsByPolicyID,
    request,
    policyExists,
  )
  return NewGrpcResponse(response)
}

export const useGetPolicyDocsFunc = (): ((
  policyModelId: string,
) => Promise<Documents>) => {
  const { auditId } = useInAudit()
  const attachmentClient = useAuthorizedGrpcClient(AttachmentPromiseClient)

  return useCallback(
    async (policyModelId: string) => {
      const request =
        new GetPolicyDocumentsByPolicyIDRequest().setPolicyModelId(
          policyModelId,
        )

      if (auditId) {
        request.setAuditId(new StringValue().setValue(auditId))
      }

      request.setUseNewModel(true)

      const response = await attachmentClient.getPolicyDocumentsByPolicyID(
        request,
      )
      return response
    },
    [auditId, attachmentClient],
  )
}

export const useMarkdownPolicyDocumentOnClick = (
  documentId: string,
  mime: string,
  contentId: string,
  setMarkdownBody: React.Dispatch<React.SetStateAction<string>>,
  linkerModelId?: LINKER_MODEL_IDS,
): React.MouseEventHandler | undefined => {
  const navigate = useNavigate()
  const location = useLocation()

  const getDocumentBodyFromDb = useDocumentOnClick({
    documentId,
    setMarkdownBody,
  })

  const getLinkedDocumentUrl = useGetLinkedDocumentUrl(contentId)
  const fetchDocumentBody = useFetchDocumentBody(contentId, mime)

  if (!contentId) {
    return getDocumentBodyFromDb
  }
  if (linkerModelId && linkerModelId.length > 0) {
    return async () => {
      const filePath = await getLinkedDocumentUrl()
      openInNewTab(filePath?.getUrl())
    }
  }

  return async () => {
    try {
      const body = await fetchDocumentBody()
      if (!body) {
        showInfoToast(ToastPrompts.DOC_DOWNLOAD_ERROR)
        return
      }
      if (isMarkdown(mime)) {
        const material = body.toString()
        editDocumentMarkdown(
          documentId,
          material,
          setMarkdownBody,
          navigate,
          location,
        )
      }
    } catch (err) {
      log.error(`Error viewing policy documentId: ${documentId}`, err)
    }
  }
}

export const useRevalidatePolicyDocuments = (modelId: string): (() => void) => {
  const { mutate } = useGetPolicyDocumentsByModelId(modelId)
  return mutate
}

export const useConnectPoliciesConfig = (): PickerConfiguration | null => {
  const ready = useLoadGoogleApi()

  if (!ready) {
    return null
  }

  return {
    allowMultiselect: false,
    viewId: google.picker.ViewId.DOCS,
    selectFolderEnabled: true,
    modalId: ModalFormId.ADD_DOCUMENT,
    mimeTypes: GOOGLE_PICKER_SUPPORTED_FILE_TYPES,
    selectableMimeTypes: 'application/vnd.google-apps.folder',
  }
}

export const useConfirmDeletePolicyDoc = ({
  documentId,
  isLinked,
  setDeleting,
}: {
  documentId: string
  isLinked: boolean
  setDeleting: React.Dispatch<React.SetStateAction<boolean>>
}): (() => void) => {
  const { setConfirmationState } = useContext(ConfirmationContext)

  const mutate = useInvalidatePolicyDocuments()
  const attachmentClient = useAuthorizedGrpcClient(AttachmentPromiseClient)
  const verb = isLinked ? 'Unlink' : 'Delete'
  const errorVerb = isLinked ? 'unlinking' : 'deleting'

  const deletePolicyDoc = async (): Promise<void> => {
    try {
      const deleteDocumentRequest = new DeleteDocumentRequest().setDocumentId(
        documentId,
      )
      await attachmentClient.deleteDocument(deleteDocumentRequest)
      await mutate()
    } catch (err) {
      setDeleting(false)
      setConfirmationState({
        isShown: false,
        title: '',
        body: '',
        confirmText: '',
        onConfirmCB: () => null,
      })
      showInfoToast(
        `There was an error ${errorVerb} the policy document. Please try again.`,
      )
    }
  }
  const confirmationModalProps = {
    title: `Are you sure you want to ${verb.toLowerCase()} the policy document?`,
    body: 'This cannot be undone.',
    confirmText: `${verb} Policy Document`,
    redirectTo: '.',
    onConfirmCB: deletePolicyDoc,
    onCancel: () => setDeleting(false),
  }

  return useConfirmationModal(confirmationModalProps)
}

export const useInvalidatePolicyDocuments = (): (() => Promise<void>) => {
  const mutateFunc = useGrpcRevalidateByMethod()

  return useCallback(async () => {
    await mutateFunc(
      AttachmentPromiseClient.prototype.getPolicyDocumentsByPolicyID,
    )
    restartPolicyPolling()
  }, [mutateFunc])
}

export const useDismissPolicyReadOnlyWarning = (
  policyId: string,
  fields: POLICY_READ_ONLY_FIELDS[],
): (() => Promise<void>) => {
  const policyClient = useAuthorizedGrpcClient(PolicyPromiseClient)

  return async () => {
    const request = new UpdatePolicyReadOnlyFieldsRequest()
      .setPolicyId(policyId)
      .setUpdates(
        new PolicyReadOnlyFields().setFieldsList([
          ...fields.map((field) =>
            new PolicyReadOnlyField()
              .setField(field) //
              .setDetails(
                new ReadOnlyFieldDetail().setIsWarningDismissed(
                  new BoolValue().setValue(true),
                ),
              ),
          ),
        ]),
      )
    await policyClient.updatePolicyReadOnlyFields(request)
  }
}

export const useImportedPolicyModalOnClick = ({
  grcPlatform,
  showModal,
  policyId,
  fields,
}: {
  grcPlatform?: GRC_PLATFORM
  showModal?: boolean
  policyId: string
  fields: POLICY_READ_ONLY_FIELDS[]
}): ((onClick: () => void) => Promise<void>) => {
  const dismissWarningFunction = useDismissPolicyReadOnlyWarning(
    policyId,
    fields,
  )

  const importedRecordModal = useImportedRecordModal({
    modelType: MODEL_TYPE.POLICY,
    grcPlatform,
    onUpdate: async (onClick: () => void) => {
      await onClick()
    },
    showModal,
    dismissWarningFunction,
  })

  return async (onClick: () => void) => {
    await importedRecordModal(onClick)
  }
}

export const useUpdatePolicyWithConfirmationModal = <T extends unknown[]>({
  callback,
  policyId,
  fields,
  grcPlatform,
  showModal,
  onCancel,
}: {
  callback: (...args: T) => Promise<void>
  policyId: string
  fields: POLICY_READ_ONLY_FIELDS[]
  grcPlatform?: GRC_PLATFORM
  showModal?: boolean
  onCancel?: () => void
}): ((...args: T) => Promise<void>) => {
  const dismissWarningFunction = useDismissPolicyReadOnlyWarning(
    policyId,
    fields,
  )

  const importedRecordModal = useImportedRecordModal({
    modelType: MODEL_TYPE.POLICY,
    grcPlatform,
    onUpdate: callback,
    showModal,
    dismissWarningFunction,
    onCancel,
  })

  return async (...args: T) => {
    await importedRecordModal(...args)
  }
}

export const useSearchablePolicies = (
  policies: PolicyRecord[],
): SearchableData<PolicyRecord>[] => {
  const { data: cfs } = useComplianceFrameworks(true)
  const getPolicyDocs = useGetPolicyDocsFunc()
  const [policyContentIdsMap, setPolicyContentIdsMap] = useState<
    Record<string, string[]>
  >({})

  useEffect(() => {
    const loadPolicyContentIds = async () => {
      const map: Record<string, string[]> = {}

      // Use Promise.all to wait for all async operations to complete
      await Promise.all(
        policies.map(async (policy) => {
          try {
            const docs = await getPolicyDocs(policy.getModelId())
            const contentIds = docs
              .getDocumentsList()
              .map((doc) => doc.getContentid())
            map[policy.getModelId()] = contentIds
          } catch (error) {
            // Handle error gracefully - set empty array for this policy
            map[policy.getModelId()] = []
          }
        }),
      )

      setPolicyContentIdsMap(map)
    }

    if (policies.length > 0) {
      loadPolicyContentIds()
    } else {
      setPolicyContentIdsMap({})
    }
  }, [policies, getPolicyDocs])

  const searchablePolicies = useMemo(() => {
    const complianceFrameworks = cfs?.getItemsList() || []
    const getPolicySearchableFields = (policy: PolicyRecord) => {
      const department = policy.getDepartmentShortName()
      const name = policy.getName()
      const frameworkIds = policy.getFrameworkIdsList()
      const complianceFrameworksForPolicy = complianceFrameworks.filter(
        (complianceFramework) => {
          return frameworkIds.includes(complianceFramework.getId())
        },
      )
      const complianceFrameworkNames =
        complianceFrameworksForPolicy.length > 0
          ? complianceFrameworksForPolicy
              .map((complianceFramework) => {
                return complianceFramework.getName()
              })
              .join(', ')
          : 'No Framework'
      const owner = policy.getOwnerEmail()

      return [department, name, complianceFrameworkNames, owner]
    }
    const getPolicyContentIds = (policy: PolicyRecord) => {
      return policyContentIdsMap[policy.getModelId()]
    }
    return prepareSearchableData(
      policies,
      (policy) => policy.getModelId(),
      getPolicySearchableFields,
      getPolicyContentIds,
    )
  }, [policies, cfs, policyContentIdsMap])

  return searchablePolicies
}

export const useGetPolicy = (policyId: string): GrpcResponse<PolicyRecord> => {
  const request = useMemo(
    () => new GetPolicyRequest().setModelId(policyId),
    [policyId],
  )
  const { response } = useSwrImmutableGrpc(
    PolicyPromiseClient.prototype.get,
    request,
  )
  return NewGrpcResponse(response)
}

export const usePollPolicy = ({
  policyId,
  checkDescriptionIndexing,
  checkNameDeptIndexing,
  checkPolicyDocDatesIndexing,
}: {
  policyId: string
  checkDescriptionIndexing?: boolean
  checkNameDeptIndexing?: boolean
  checkPolicyDocDatesIndexing?: boolean
}): {
  policy: PolicyRecord | undefined
  isPolling: boolean
  isIndexing: boolean
  isLoading: boolean
} => {
  // Polling may start immediately, but we only want the UI to show "indexing"
  // once we've actually observed an indexing flag.
  const [isPolling, setIsPolling] = useState(true)
  const [isIndexing, setIsIndexing] = useState(false)
  const { data, isLoading, mutate } = useGetPolicy(policyId)
  const mutateRef = useRef(mutate)
  const startedIndexingRef = useRef(false)
  mutateRef.current = mutate
  const initialFieldsRef = useRef<PolicyFieldSnapshot | null>(null)

  useEffect(() => {
    const restart = () => {
      initialFieldsRef.current = null
      startedIndexingRef.current = true
      setIsIndexing(true)
      setIsPolling(true)
    }
    policyPollRestartCallbacks.add(restart)
    return () => {
      policyPollRestartCallbacks.delete(restart)
    }
  }, [])

  useEffect(() => {
    if (!isPolling) return

    const POLL_INTERVAL_MS = 10000
    const MAX_GRACE_POLLS = 3
    let cancelled = false
    let timeoutId: NodeJS.Timeout
    let gracePollCount = 0

    const poll = async () => {
      if (cancelled) return
      try {
        const updated = await mutateRef.current()
        if (cancelled) return

        const isIndexing =
          !updated ||
          (checkDescriptionIndexing && updated.getIsDescriptionIndexing()) ||
          (checkNameDeptIndexing && updated.getIsNameDeptIndexing()) ||
          (checkPolicyDocDatesIndexing &&
            updated.getArePolicyDocDatesIndexing())

        const currentFields = getPolicyFieldSnapshot(updated)

        if (initialFieldsRef.current === null) {
          initialFieldsRef.current = currentFields
        }

        const hasChanged = policyFieldsChanged(
          initialFieldsRef.current,
          currentFields,
        )

        if (isIndexing) {
          startedIndexingRef.current = true
          setIsIndexing(true)
          gracePollCount = 0
          timeoutId = setTimeout(poll, POLL_INTERVAL_MS)
        } else if (
          startedIndexingRef.current &&
          !hasChanged &&
          gracePollCount < MAX_GRACE_POLLS
        ) {
          gracePollCount += 1
          timeoutId = setTimeout(poll, POLL_INTERVAL_MS)
        } else {
          startedIndexingRef.current = false
          setIsIndexing(false)
          setIsPolling(false)
        }
      } catch {
        if (cancelled) return
        timeoutId = setTimeout(poll, POLL_INTERVAL_MS)
      }
    }

    poll()

    return () => {
      cancelled = true
      startedIndexingRef.current = false
      setIsIndexing(false)
      clearTimeout(timeoutId)
    }
  }, [isPolling]) // eslint-disable-line react-hooks/exhaustive-deps

  return { policy: data, isPolling, isIndexing, isLoading }
}

/**
 * Tracks in-progress receptor scans that correspond to policy document folder scans,
 * polls while running, and refreshes policy and directory-scan caches when a run completes.
 * After scan completion, continues polling until async indexing (name/dept/description
 * generation) finishes and policy data has actually updated.
 */
export function usePolicyFolderScanListRefresh(request: ListPoliciesRequest): {
  policyFolderScanInProgress: boolean
  isIndexing: boolean
} {
  const { data: directoryScanSettings } = useListDirectoryScanSettings()
  const [isIndexing, setIsIndexing] = useState(false)
  const { data: policiesData, mutate: mutatePolicies } = usePolicies(request)
  const currentPolicies = useMemo(
    () => policiesData?.getItemsList() || [],
    [policiesData],
  )
  const previousSnapshotRef = useRef<PolicyListSnapshot | null>(null)

  // Policy folder scan row ids (match ListInProgressScans import_keys from fetch_directory_policies)
  const policyFolderScanSettingIds = useMemo(() => {
    const ids = new Set<string>()
    directoryScanSettings?.getSettingsList().forEach((s) => {
      if (s.getDocumentType() === DOCUMENT_TYPE.POLICYDOC) {
        const id = s.getId()?.trim()
        if (id) ids.add(id)
      }
    })
    return ids
  }, [directoryScanSettings])

  // Track in-progress scans, polling while a scan is running
  const { data: inProgressScans, mutate: mutateInProgressScans } =
    useInProgressReceptorScans(true, {
      refreshInterval:
        policyFolderScanSettingIds.size > 0 || isIndexing ? 5000 : 0,
    })

  const policyFolderScanInProgress = useMemo(() => {
    if (policyFolderScanSettingIds.size === 0) return false
    const scans = inProgressScans?.getScansList() || []
    return scans.some((scan) => {
      const keys = scan.getImportKeysList()
      if (!keys.length) return false
      return keys.some((key) => policyFolderScanSettingIds.has(key))
    })
  }, [inProgressScans, policyFolderScanSettingIds])

  const invalidatePolicies = useInvalidatePoliciesCache()
  const invalidateDirectoryScan = useInvalidateDirectoryScanSettings()

  // Refresh policies while scan is in progress
  useEffect(() => {
    if (!policyFolderScanInProgress) return
    const intervalId = window.setInterval(() => {
      void invalidatePolicies()
    }, POLL_INTERVAL_MS)
    return () => clearInterval(intervalId)
  }, [policyFolderScanInProgress, invalidatePolicies])

  // Transition: scan completes → enter post-scan indexing phase
  const prevInProgressRef = useRef(false)
  useEffect(() => {
    if (prevInProgressRef.current && !policyFolderScanInProgress) {
      previousSnapshotRef.current = snapshotPolicies(currentPolicies)
      void Promise.all([
        invalidatePolicies(),
        invalidateDirectoryScan(),
        mutateInProgressScans(),
      ])
      setIsIndexing(true)
    }
    prevInProgressRef.current = policyFolderScanInProgress
  }, [
    currentPolicies,
    policyFolderScanInProgress,
    invalidatePolicies,
    invalidateDirectoryScan,
    mutateInProgressScans,
  ])

  useEffect(() => {
    if (policyFolderScanInProgress || isIndexing) return
    if (isAnyPolicyIndexing(currentPolicies)) {
      previousSnapshotRef.current = snapshotPolicies(currentPolicies)
      setIsIndexing(true)
      return
    }
    previousSnapshotRef.current = snapshotPolicies(currentPolicies)
  }, [currentPolicies, policyFolderScanInProgress, isIndexing])

  // Post-scan indexing poll loop (matches usePollPolicy / usePollControlEvidenceMappings pattern)
  const mutateRef = useRef(mutatePolicies)
  mutateRef.current = mutatePolicies

  useEffect(() => {
    if (!isIndexing) return

    let cancelled = false
    let timeoutId: NodeJS.Timeout
    let stablePollCount = 0
    const poll = async () => {
      if (cancelled) return
      try {
        // Refresh all policy caches so grid rows see updated names/depts
        await invalidatePolicies()
        if (cancelled) return

        const updated = await mutateRef.current()
        if (cancelled) return

        const policies = updated?.getItemsList() || []
        const anyIndexing = isAnyPolicyIndexing(policies)
        const currentSnapshot = snapshotPolicies(policies)
        const isStable =
          previousSnapshotRef.current !== null &&
          getArePolicySnapshotsEqual(
            previousSnapshotRef.current,
            currentSnapshot,
          )

        previousSnapshotRef.current = currentSnapshot

        if (anyIndexing || !isStable) {
          stablePollCount = 0
          timeoutId = setTimeout(poll, POLL_INTERVAL_MS)
        } else if (stablePollCount < MAX_STABLE_POLLS) {
          stablePollCount += 1
          timeoutId = setTimeout(poll, POLL_INTERVAL_MS)
        } else {
          setIsIndexing(false)
        }
      } catch {
        if (cancelled) return
        timeoutId = setTimeout(poll, POLL_INTERVAL_MS)
      }
    }

    poll()

    return () => {
      cancelled = true
      clearTimeout(timeoutId)
    }
  }, [isIndexing]) // eslint-disable-line react-hooks/exhaustive-deps

  return { policyFolderScanInProgress, isIndexing }
}
